import React from 'react';
import { Interview, InterviewStage, InterviewStageName } from '../../types/interview';
import { motion } from 'framer-motion';
import { FiArrowRight, FiArrowLeft, FiMessageSquare } from 'react-icons/fi';

interface InterviewCardProps {
  interview: Interview;
  stage: InterviewStage;
  onStageChange: (stageId: number, newStage: InterviewStageName) => void;
  onStageComplete: (stageId: number) => Promise<void>;
  color: string;
  onOpenNotes: () => void;
  allStages: InterviewStageName[];
}

const InterviewCard: React.FC<InterviewCardProps> = ({
  interview,
  stage,
  onStageChange,
  onStageComplete,
  color,
  onOpenNotes,
  allStages
}) => {
  const currentStageIndex = allStages.indexOf(interview.stage as InterviewStageName);

  const handleMoveNext = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStageIndex < allStages.length - 1) {
      const nextStage = allStages[currentStageIndex + 1];
      await onStageChange(stage.id, nextStage);
      setTimeout(() => {
        const nextColumn = document.querySelector(`[data-stage="${nextStage}"]`);
        nextColumn?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  };

  const handleMovePrev = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStageIndex > 0) {
      const prevStage = allStages[currentStageIndex - 1];
      await onStageChange(stage.id, prevStage);
      setTimeout(() => {
        const prevColumn = document.querySelector(`[data-stage="${prevStage}"]`);
        prevColumn?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  };

  return (
    <motion.div
      onClick={onOpenNotes}
      role="button"
      tabIndex={0}
      className={`bg-white rounded-lg shadow-sm p-4 border-l-4 border-${color}-500 hover:shadow-md transition-shadow cursor-pointer`}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      layout
      layoutId={`interview-${interview.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-gray-900">
            {`${interview.stage.charAt(0).toUpperCase() + interview.stage.slice(1)} Interview`}
          </h4>
          {interview.scheduled_at && (
            <p className="text-sm text-gray-600 mt-1">
              {new Date(interview.scheduled_at).toLocaleString()}
            </p>
          )}
        </div>
        <span className={`px-2 py-1 text-sm font-medium text-${color}-700 bg-${color}-100 rounded-full`}>
          {interview.stage}
        </span>
      </div>

      <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
        <div className="flex gap-2">
          <button
            onClick={handleMovePrev}
            disabled={currentStageIndex === 0}
            className={`p-1 rounded-full ${
              currentStageIndex === 0
                ? 'text-gray-300'
                : `text-${color}-600 hover:bg-${color}-50`
            }`}
            title={currentStageIndex > 0 ? `Move to ${allStages[currentStageIndex - 1]}` : ''}
          >
            <FiArrowLeft className="w-4 h-4" />
          </button>
          <button
            onClick={handleMoveNext}
            disabled={currentStageIndex === allStages.length - 1}
            className={`p-1 rounded-full ${
              currentStageIndex === allStages.length - 1
                ? 'text-gray-300'
                : `text-${color}-600 hover:bg-${color}-50`
            }`}
            title={currentStageIndex < allStages.length - 1 ? `Move to ${allStages[currentStageIndex + 1]}` : ''}
          >
            <FiArrowRight className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenNotes();
          }}
          className={`flex items-center p-1 rounded-full text-${color}-600 hover:bg-${color}-50`}
        >
          <FiMessageSquare className="w-4 h-4" />
          <span className="ml-1 text-sm">{interview.notes.length}</span>
        </button>
      </div>
    </motion.div>
  );
};

export default InterviewCard;